/**
 * Page previews: watermarked images of a few pages of a product's PDF, shown on
 * its product page under "Look inside", so a buyer can judge a worksheet or a
 * guide by its pages before paying, as every rival marketplace lets them.
 *
 * MADE IN THE SELLER'S BROWSER, never on the server: the listing form renders
 * the pages with pdf.js (src/lib/pdf-page-render.ts, a self-hosted copy under
 * public/vendor/), stamps the watermark, and uploads the images with the rest
 * of the product's media when it saves. The PDF itself never leaves the
 * seller's machine for this, and the server needs no PDF renderer.
 *
 * MADE AUTOMATICALLY, AND NEVER MUCH OF THE PRODUCT. When a seller adds a PDF,
 * the form makes previews of its first pages straight away (autoPreviewPages:
 * a third of the document, at most three pages, none for a document under
 * three pages). The seller sees them before saving and can choose other pages,
 * up to half the document and never more than five (maxPreviewPages), or
 * remove them.
 *
 * STORED as `pagePreviews` on products/{id}: up to five {url, path, page,
 * width, height}, which firestore.rules bounds (pagePreviewsWithinBounds), and
 * `pdfPageCount`, the PDF's length, which the page shows as "24 pages". The
 * images live at products/{id}/page-previews/ (storage.rules), and `path` is
 * what lets onProductUpdated delete an image once a save drops it
 * (extractAllFilePaths in functions, collectStoragePaths here).
 *
 * This file is pure, so its rules are testable without a browser.
 */

/** The pdf.js build under public/vendor/. Bumping it means copying the new build's two files there. */
export const PDFJS_VERSION = '6.4.299';
export const PDFJS_BASE_PATH = `/vendor/pdfjs-${PDFJS_VERSION}`;

/** The most pages a product shows. */
export const MAX_PREVIEW_PAGES = 5;

/**
 * The largest PDF the form draws previews from. pdf.js reads the whole file
 * into the tab's memory first, and a phone or an old laptop runs out well
 * before a deliverable's 5 GB cap; a classroom PDF is a few megabytes.
 */
export const MAX_PREVIEW_PDF_BYTES = 150 * 1024 * 1024;

/** The most pages made without asking. */
export const AUTO_PREVIEW_PAGES = 3;

/**
 * The longer side of a preview image, in pixels: enough to read a page's
 * layout and headings on a phone or a laptop, too little to print a usable copy.
 */
export const PREVIEW_LONG_EDGE_PX = 1100;

/** Where a product's previews are uploaded (storage.rules allows images only, 3 MB each). */
export function pagePreviewFolder(productId: string): string {
  return `products/${productId}/page-previews`;
}

export type PagePreview = {
  /** The image's download URL. */
  url: string;
  /** Its storage path, for cleanup. */
  path: string;
  /** Which page of the PDF it shows, from 1. */
  page: number;
  width: number;
  height: number;
};

/** How many pages a seller may show for a document of `total` pages. */
export function maxPreviewPages(total: number): number {
  if (!Number.isFinite(total) || total < 1) return 0;
  return Math.max(1, Math.min(MAX_PREVIEW_PAGES, Math.floor(total / 2)));
}

/** The pages made automatically: the first third of the document, at most three, none under three pages. */
export function autoPreviewPages(total: number): number[] {
  if (!Number.isFinite(total) || total < 3) return [];
  const count = Math.min(AUTO_PREVIEW_PAGES, Math.floor(total / 3));
  return Array.from({ length: count }, (_, i) => i + 1);
}

/**
 * The pages a seller asked for, made safe: whole page numbers inside the
 * document, no repeats, in page order, at most maxPreviewPages(total).
 */
export function clampPreviewPages(pages: readonly number[], total: number): number[] {
  const unique = [...new Set(pages.filter((page) => Number.isInteger(page) && page >= 1 && page <= total))];
  return unique.sort((a, b) => a - b).slice(0, maxPreviewPages(total));
}

/** The render scale that gives a page of this size (in PDF points) a PREVIEW_LONG_EDGE_PX long side. */
export function previewScale(width: number, height: number): number {
  const longEdge = Math.max(width, height);
  return longEdge > 0 ? PREVIEW_LONG_EDGE_PX / longEdge : 1;
}

/** The watermark's font size on an image of this size. */
export function watermarkFontSize(width: number, height: number): number {
  return Math.max(14, Math.round(Math.min(width, height) / 18));
}

/**
 * Where the watermark's text goes on an image of this size: diagonal rows of
 * the same line across the whole page, so cropping one corner off does not
 * remove it. Positions are the centre of each line before the rotation.
 *
 * @param textWidth The line's drawn width at watermarkFontSize, as the canvas
 *   measures it, so repeats in a row never run into each other.
 */
export function watermarkLayout(width: number, height: number, textWidth = 0): {
  fontSize: number;
  angle: number;
  positions: Array<{ x: number; y: number }>;
} {
  const fontSize = watermarkFontSize(width, height);
  const angle = -Math.PI / 6;
  const stepX = Math.max(fontSize * 11, Math.ceil(textWidth) + fontSize * 2);
  const stepY = fontSize * 5;
  // Cover the rotated page: the diagonal is the farthest any row can reach.
  const reach = Math.hypot(width, height);
  const positions: Array<{ x: number; y: number }> = [];
  let row = 0;
  for (let y = -reach / 2; y <= reach / 2; y += stepY, row++) {
    const offset = row % 2 === 0 ? 0 : stepX / 2;
    for (let x = -reach / 2 - offset; x <= reach / 2; x += stepX) {
      positions.push({ x: Math.round(x), y: Math.round(y) });
    }
  }
  return { fontSize, angle, positions };
}

/** The watermark's line: the site, and the seller's storefront handle when there is one. */
export function watermarkText(username: string | null | undefined): string {
  const handle = typeof username === 'string' ? username.trim().replace(/^@/, '') : '';
  return handle ? `PREVIEW · supa.place/@${handle}` : 'PREVIEW · supa.place';
}

function isPositiveInt(value: unknown, max: number): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= max;
}

/** A stored list as previews to render: well-formed entries only, in page order, at most MAX_PREVIEW_PAGES. */
export function pagePreviewsOf(raw: unknown): PagePreview[] {
  if (!Array.isArray(raw)) return [];
  const out: PagePreview[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const { url, path, page, width, height } = entry as Record<string, unknown>;
    if (typeof url !== 'string' || !url.startsWith('https://')) continue;
    if (!isPositiveInt(page, 100_000) || !isPositiveInt(width, 4000) || !isPositiveInt(height, 4000)) continue;
    out.push({ url, path: typeof path === 'string' ? path : '', page, width, height });
  }
  return out.sort((a, b) => a.page - b.page).slice(0, MAX_PREVIEW_PAGES);
}

/** The stored page count, or null. */
export function pdfPageCountOf(raw: unknown): number | null {
  return isPositiveInt(raw, 100_000) ? raw : null;
}

/** "24 pages", "1 page". */
export function pageCountLabel(count: number): string {
  return `${count.toLocaleString('en-AU')} ${count === 1 ? 'page' : 'pages'}`;
}

/** Whether a file is a PDF, by type or, for a browser that reports none, by name. */
export function isPdfFile(file: { name?: string; type?: string } | null | undefined): boolean {
  if (!file) return false;
  if (file.type === 'application/pdf') return true;
  return typeof file.name === 'string' && /\.pdf$/i.test(file.name);
}
